<template>
    <div>
        <PositionRolesAndResponsibilitiesDraggableTree
            v-if="useNewTree"
            class="position-attached-roles-tree"
            data-testid="position-attached-roles-tree"
            :state="treeState"
            :rows="treeRows"
            :filters="treeFilters"
            :view="treeView"
            :position-name="positionName"
            :account-id="accountId"
            @attach="handleOpenAttachRoleDialog"
            @attach-responsibility="handleOpenAttachResponsibilityToPositionDialog"
            @detach="handleTreeDetach"
            @filters-change="handleTreeFiltersChange"
            @apply-filters="handleTreeApplyFilters"
            @reset-filters="handleTreeResetFilters"
            @clear-filters="handleTreeClearFilters"
            @open-chatter="handleTreeOpenChatter"
            @row-menu-action="handleTreeRowMenuAction"
            @converted="reloadCurrentPage"
            @reorder="handleTreeReorder"
            @item-selected="handleTreeItemSelected"
            @view-change="handleTreeViewChange"
            @drilldown="handleTreeCardDrilldown"
            @role-drilldown="handleRoleDrillDown"
            @import-job-description="handleImportJobDescription"
            @role-data-updated="reloadCurrentPage"
            @responsibility-data-updated="reloadCurrentPage"
        />
        <OrganizationRolesTable
            v-else
            ref="organizationRolesTableRef"
            :pagination="paginationComputed" 
            :useNewBadge="true" 
            :always-show-horizontal-scrollbar="true"
            :dataStream="rolesData" 
            :loading="skeletonLoading"
            :initialFilters="appliedFilters"
            :disable-attached-roles-actions="positionArchived"
            :tooltip-for-disabled-attached-roles-actions="positionArchived && showTooltipWhenAttachedRolesBlocked"
            :showAttachToPositionsBulk="true"
            :show_detach_button="true"
            :show-detach-button="true"
            :detachContextObject="detachContextObject"
            :add_button_text="'Attach'"
            :emit_add_action="true"
            :emit_role_drilldown="true"
            :allow-drag-role-to-tree="drilldownInPanel && allowDragRoleToPositionTree"
            :source-position-id-for-role-drag="positionId"
            :on-role-drag-start-callback="onRoleDragStartCallback"
            :on-role-drag-end-callback="onRoleDragEndCallback"
            :enable-attached-roles-reorder="enableAttachedRolesReorder"
            :chatterData="chatterData"
            :totalChatterData="totalChatterData"
            @onSearchChange="handleSearchChange"
            @onFilterChange="handleFilterChange"
            @onPageChange="handlePageChange"
            @onRowOperation="handleRowOperation"
            @onAddRoleClick="handleOpenAttachRoleDialog"
            @onRoleDrillDown="handleRoleDrillDown"
            @openChatterDrawer="handleOpenChatterDrawer"
            @detach="handleDetach"
            @detach-inline="handleDetachInline"
            @requestMoreCheckpointsChatter="handleRequestMoreCheckpointsChatter"
            @onReorderAttachedRoles="handleReorderAttachedRoles"
        />
        <ChatterDrawer
            v-if="useNewTree"
            :drawer="treeChatterOpen"
            :type="currentChatterType"
            :typeValue="treeChatterTypeValue"
            :iconName="treeChatterIconName"
            :listData="treeChatterListData"
            :object-type="currentChatterType"
            :object-id="treeChatterListData?.id ?? null"
            :chatterList="chatterData"
            :totalCheckpointChatter="totalChatterData"
            :loading="treeChatterLoading"
            @closeDrawer="handleTreeCloseChatter"
            @requestMoreCheckpointsChatter="handleRequestMoreCheckpointsChatter"
        />

        <AttachedRole
            v-model="showAttachRoleDialog"
            OrgObjectType="Role to Position"
            @onSave="attachSelectedRoleToPosition"
            :items="attachableRoles"
            @item-selected="handleRoleSelected"
            :existingNamesList="attachedRoleNames"
            @add-item-selected="handleAddNewRole"
            :parentName="positionDataObject.name || ''"
            :openDefault="openDefaultAttachDialog"
            @input="closeAttachRoleDialog"
            :showError="attachRoleError"
            :isLoadingMore="isLoadingMoreRoles"
            :hasMoreData="hasMoreRolesData"
            @load-more="loadMoreRoles"
            @search="searchRoles"
            :type="'attach_role_to_position_new_picker'"
            :isAssigning="isAssigningRole"
            :position="addAndAttachPosition"
            @created-and-attached="handleRoleCreatedAndAttached"
        />

        <ActivateRoleDialog
            :value="showActivateRoleDialog"
            @input="showActivateRoleDialog = $event"
            @onSave="onActivateRoleConfirm"
            :name="currentRole?.role_name || currentRole?.data?.name || ''"
            :employee-count="roleActivationEmployeeCount"
        />
        <ActivateResponsibilityConfirmationModal
            :dialog="showActivateResponsibility"
            @close="showActivateResponsibility = false"
            @skip="skipActivateResponsibility"
            @activate="activateAttachedResponsibilities"
            :role="currentRole?.role_name || currentRole?.data?.name || ''"
            :draftResponsibilityCount="draftResponsibilityCount"
            :skipButtonLoader="skipActivateButtonLoader"
            :activateButtonLoader="activateAttachedButtonLoader"
        />
        <RetireRoleDialog
            :show="showRetireRoleDialog"
            @onClose="showRetireRoleDialog = false"
            @onRetire="retireRole"
            :name="currentRole?.role_name || currentRole?.data?.name || ''"
            :role="currentRole"
        />
        <ReactivateRoleDialog
            :show="showReActivateRoleDialog"
            @onClose="showReActivateRoleDialog = false"
            @onReactivate="reActivateRole"
            :name="currentRole?.role_name || currentRole?.data?.name || ''"
            :role="currentRole"
            :employee-count="roleActivationEmployeeCount"
        />
        <RenameRoleDialog
            v-if="showRenameRoleDialog && renameRoleModel"
            v-model="showRenameRoleDialog"
            :role="renameRoleModel"
            :existingNames="existingNames"
            @saved="onRoleRenamed"
        />
        <DeleteRoleDialog
            :show="showDeleteRoleDialog"
            @onClose="showDeleteRoleDialog = false"
            @onDelete="deleteRole"
            :name="currentRole?.role_name || currentRole?.data?.name || ''"
        />
        <ArchivePopUp
            :action="archiveAction"
            :type="archiveObjectType"
            :show="showArchiveDialog"
            :curretObj="archiveTarget"
            @onClose="showArchiveDialog = false"
            @onArchive="onArchiveConfirm"
        />
        <ActivateResponsibility
            :value="showActivateResponsibilityDialog"
            @input="showActivateResponsibilityDialog = $event"
            :responsibility="currentResponsibilityName"
            :employee-count="responsibilityActivationEmployeeCount"
            @onSave="onActivateResponsibility"
        />
        <RetireResponsibility
            :value="showRetireResponsibilityDialog"
            @input="showRetireResponsibilityDialog = $event"
            :responsibility="currentResponsibilityName"
            @onSave="onRetireResponsibility"
        />
        <ReActivateResponsibility
            :value="showReActivateResponsibilityDialog"
            @input="showReActivateResponsibilityDialog = $event"
            :responsibility="currentResponsibilityName"
            :employee-count="responsibilityActivationEmployeeCount"
            @onSave="onReActivateResponsibility"
        />
        <RenameResponsibilityDialog
            v-if="showRenameResponsibilityDialog && renameResponsibilityModel"
            :value="showRenameResponsibilityDialog"
            @input="showRenameResponsibilityDialog = $event"
            :responsibility="renameResponsibilityModel"
            @saved="onResponsibilityRenamed"
        />
        <DeleteResponsibility
            :value="showDeleteResponsibilityDialog"
            @input="showDeleteResponsibilityDialog = $event"
            :responsibility="currentResponsibilityName"
            @onDelete="onDeleteResponsibility"
        />
        <ExActionsPopUp
            :value="detachConfirm.show"
            :headline="detachConfirmCopy.headline"
            :icon="detachConfirmCopy.icon"
            :info="detachConfirmCopy.info"
            :destructive="true"
            actionLabel="Detach"
            cancelLabel="Cancel"
            :applyBorderClass="true"
            :centerHeadline="true"
            :width="500"
            actionsTopSpacing="32px"
            @input="closeDetachConfirm"
            @onAction="confirmDetach"
            data-testid="detach-position-role-confirmation"
        />
        <AttachedResponsibility
            v-if="directResponsibilityAttachEnabled"
            v-model="showAttachResponsibilityToPositionDialog"
            OrgObjectType="Responsibility to Position"
            @onSave="handleAttachResponsibilityToPosition"
            :items="attachableResponsibilitiesForPosition"
            @item-selected="handlePositionResponsibilitySelected"
            @add-item-selected="handleAddNewResponsibilityToPosition"
            :parentName="positionDataObject.name || ''"
            :openDefault="openDefaultAttachResponsibilityToPositionDialog"
            @input="closeAttachResponsibilityToPositionDialog"
            :type="
                showAttachResponsibilitiesPicker
                    ? 'attach_responsibility_role_new_picker'
                    : 'attach_responsibility_role'
            "
            @load-more="loadMoreResponsibilitiesForPosition"
            @search="searchResponsibilitiesForPosition"
            :hasMoreData="hasMoreResponsibilitiesForPositionData"
            :isLoadingMore="isLoadingMoreResponsibilitiesForPosition"
            :isAssigning="isAssigningResponsibilityToPosition"
        />
        <AttachedResponsibility
            v-model="showAttachResponsibilityDialog"
            @onSave="handleAttachResponsibilityToRole"
            :items="attachableResponsibilities"
            @item-selected="handleResponsibilitySelected"
            @add-item-selected="handleAddNewResponsibility"
            :parentName="currentRole?.role_name || currentRole?.data?.name || ''"
            :openDefault="openDefaultAttachResponsibilityDialog"
            @input="closeAttachResponsibilityDialog"
            :type="
                showAttachResponsibilitiesPicker
                    ? 'attach_responsibility_role_new_picker'
                    : 'attach_responsibility_role'
            "
            @load-more="loadMoreResponsibilities"
            @search="searchResponsibilities"
            :hasMoreData="hasMoreResponsibilitiesData"
            :isLoadingMore="isLoadingMoreResponsibilities"
            :isAssigning="isAssigningResponsibility"
        />
        <AttachRoleToPositionsInBulkDialog
            v-model="showAttachRoleToPositionsBulkDialog"
            :role-name="currentRole?.role_name || currentRole?.data?.name || ''"
            action-type="attach"
            :positions="bulkAttachPositions"
            :loading="isBulkAttachPositionsLoading"
            :total="bulkAttachTotalPositions"
            :per-page="10"
            @onRequest="handleBulkAttachPositionRequest"
            @onSubmit="handleBulkAttachRoleToPositions"
            @onAddPosition="onBulkAttachAddPosition"
            @close="closeAttachRoleToPositionsBulkDialog"
        />
    </div>
</template>

<script setup>
import { defineProps, ref, onMounted, computed, getCurrentInstance, watch } from "vue";
import OrganizationRolesTable from "@components/OrganizationRolesTable/OrganizationRolesTable.vue";
import PositionRolesAndResponsibilitiesDraggableTree from "@components/PositionRolesAndResponsibilitiesDraggableTree/PositionRolesAndResponsibilitiesDraggableTree.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import { convertRoleFiltersToApiFormat } from "@components/FilterRolesModal/roleFilterPayload";
import {
    LD_FLAG_POSITION_ROLES_RESPONSIBILITIES_TREE,
    LD_FLAG_IDEA_MDP_585,
    LD_FLAG_IDEA_MDP_560,
    LD_FLAG_IDEA_MDP_495,
} from "@components/constants/launchDarklyFlagKeys";
import AttachedRole from "@components/RolesCatalog/AttachedRole.vue";
import ActivateRoleDialog from "@components/ActivateRoleDialog/ActivateRoleDialog.vue";
import ActivateResponsibilityConfirmationModal from "@components/ActivateResponsibilityConfirmationModal/ActivateResponsibilityConfirmationModal.vue";
import RetireRoleDialog from "@components/RetireRoleDialog/RetireRoleDialog.vue";
import ReactivateRoleDialog from "@components/ReactivateRoleDialog/ReactivateRoleDialog.vue";
import RenameRoleDialog from "@components/RenameRoleDialog/RenameRoleDialog.vue";
import DeleteRoleDialog from "@components/DeleteRoleDialog/DeleteRoleDialog.vue";
import ArchivePopUp from "@components/common/ArchivePopUp.vue";
import ActivateResponsibility from "@components/RolesCatalog/ActivateResponsibility.vue";
import RetireResponsibility from "@components/RolesCatalog/RetireResponsibility.vue";
import ReActivateResponsibility from "@components/RolesCatalog/ReActivateResponsibility.vue";
import RenameResponsibilityDialog from "@components/RolesCatalog/RenameResponsibilityDialog/RenameResponsibilityDialog.vue";
import { toRenameResponsibilityModel } from "@components/utils/renameResponsibilityModel";
import DeleteResponsibility from "@components/RolesCatalog/DeleteResponsibility.vue";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import AttachedResponsibility from "@components/RolesCatalog/AttachedRole.vue";
import AttachRoleToPositionsInBulkDialog from "@components/AttachRoleToPositionsInBulkDialog/AttachRoleToPositionsInBulkDialog.vue";
import {
    getListOfRolesAttachedToPosition,
    getRoleAttachmentsForPosition,
    getListOfResponsibilitiesAttachedToRole,
    getChatterList,
    detachOrgObject,
    detachRoleAttachment,
    fetchAttachableRolesForPosition,
    attachRoleToPosition,
    getListOfPositionsV2,
    getListOfPositionsAttachedToRole,
    createRole,
    updateActiveRole,
    updateRetiredRole,
    updateReActiveRole,
    deleteReadinessRole,
    deleteReadinessRoleWithResponsibilities,
    updateArchiveRole,
    updateUnArchiveRole,
    updateActivateResponsibility,
    updateRetireResponsibility,
    updateReActivateResponsibility,
    deleteResponsibility,
    updateArchiveResponsibility,
    updateUnArchiveResponsibility,
    fetchAttachableResponsibilities,
    attachResponsibilityToRole,
    attachResponsibilityToPosition,
    getDirectResponsibilitiesAttachedToPosition,
    fetchAttachableResponsibilitiesForPosition,
    detachResponsibilityFromPosition,
    reorderRolesOnPosition,
    reorderPositionAttachments,
    reorderRoleResponsibilities,
    reOrderSequenceData,
} from "@/api";
import { useIQOverlayStore } from "@/store/pinia/iqOverlay";
import { fetchRoleActivationCount, fetchResponsibilityActivationCount } from "@/utils/activationEmployeeCount";
import { getDeleteErrorMessage } from "@components/utils/getDeleteErrorMessage";

const instance = getCurrentInstance();
const $loader = instance?.proxy?.$loader;
const $notifications = instance?.proxy?.$notifications;
const iqOverlayStore = useIQOverlayStore();

const props = defineProps({
    positionId: {
        required: false,
        default: null,
        validator: (v) =>
            v === null ||
            v === undefined ||
            typeof v === "number" ||
            typeof v === "string",
    },
    positionName: {
        type: String,
        default: '',
    },
    /** When the position is archived, attached-role mutations are blocked in the table UI. */
    positionArchived: {
        type: Boolean,
        default: false,
    },
    /**
     * When the position blocks attached-role actions (archived), also show EXTooltip
     * on disabled controls. When false, actions stay disabled without tooltips.
     */
    showTooltipWhenAttachedRolesBlocked: {
        type: Boolean,
        default: false,
    },
    /** When true (e.g. Positions Tree View), drilldown stays in same panel */
    drilldownInPanel: {
        type: Boolean,
        default: false,
    },
    onRoleDragStartCallback: {
        type: Function,
        default: null,
    },
    onRoleDragEndCallback: {
        type: Function,
        default: null,
    },
    /** LaunchDarkly gate for tree drag-drop (combined with drilldownInPanel). */
    allowDragRoleToPositionTree: {
        type: Boolean,
        default: true,
    },
    /** DevExtreme row reorder + unified grip (pane: gated with allowDragRoleToPositionTree via PositionForm). */
    enableAttachedRolesReorder: {
        type: Boolean,
        default: true,
    },
    /** Position code shown in the create-and-attach Role dialog ("Attaching to Position"). */
    positionCode: {
        type: [String, Number],
        default: '',
    },
});

const rolesData = ref([]);
const paginationModel = ref({
    page: 1,
    total: 0,
    perPage: 10,
});

const searchQuery = ref("");
const organizationRolesTableRef = ref(null);
const appliedFilters = ref(["draft", "active"]);
const accountId = ref(null);
const isLoading = ref(false);
// ER-10592 / MDP-560 — gate the attached-roles table skeleton behind the consistent
// skeleton-loading rollout flag so the legacy empty-state flash is preserved when off.
const skeletonLoadingEnabled = ref(false);
const skeletonLoading = computed(() => isLoading.value && skeletonLoadingEnabled.value);
const useNewTree = ref(false);
const directResponsibilityAttachEnabled = ref(false);
// ER-9940: idea-MDP-495 kill switch (default on) for the red invalid-role name.
const validityIndicatorsEnabled = ref(true);

// Carries the backend verdict onto a row only while the flag is on; never recomputed client-side.
function pickValidityFields(role) {
    if (!validityIndicatorsEnabled.value || typeof role?.is_valid !== "boolean") return {};
    return { is_valid: role.is_valid, invalidity_reason: role.invalidity_reason || "" };
}
const treeRows = ref([]);
const treeView = ref("tree");
const treeFilters = ref({
    status: ["Active", "Draft"],
    archived: "Do Not Show",
    types: [],
    search: "",
});
const treeState = computed(() => (treeRows.value.length > 0 ? "filled" : "empty"));
const currentChatterType = ref("role");
const emit = defineEmits([
    'drilldown',
    'refresh-position-data',
    'attached-roles-search-sync',
    'position-attached-roles-sync',
]);

// Chatter related state
const chatterData = ref([]);
const totalChatterData = ref(0);
const loadedChatterPages = ref({});
const currentChatterRoleId = ref(null);
const treeChatterOpen = ref(false);
const treeChatterLoading = ref(false);
const treeChatterTypeValue = ref("");
const treeChatterListData = ref(null);
const treeChatterIconName = computed(() =>
    currentChatterType.value === "responsibility" ? "org_icon_responsibility" : "org_icon_role"
);

// Attach role state
const showAttachRoleDialog = ref(false);
const attachRoleError = ref("");
const attachableRoles = ref([]);
const hasMoreRolesData = ref(false);
const isLoadingMoreRoles = ref(false);
const isAssigningRole = ref(false);
const selectedRoleId = ref(null);
const rolesPage = ref(1);
const rolesSearchTerm = ref("");
const openDefaultAttachDialog = ref(false);

// Role action dialog state
const showActivateRoleDialog = ref(false);
const showActivateResponsibility = ref(false);
const skipActivateButtonLoader = ref(false);
const activateAttachedButtonLoader = ref(false);
const showRetireRoleDialog = ref(false);
const showReActivateRoleDialog = ref(false);
const roleActivationEmployeeCount = ref(0);
const showRenameRoleDialog = ref(false);
const showDeleteRoleDialog = ref(false);
const showArchiveDialog = ref(false);
const archiveAction = ref(null);
const archiveObjectType = ref("role");
const currentRole = ref(null);

// Responsibility row-menu dialog state (tree "⋯" menu on responsibility rows)
const currentResponsibility = ref(null);
const renameResponsibilityModel = computed(() =>
    toRenameResponsibilityModel(currentResponsibility.value)
);
const showActivateResponsibilityDialog = ref(false);
const showRetireResponsibilityDialog = ref(false);
const showReActivateResponsibilityDialog = ref(false);
const showRenameResponsibilityDialog = ref(false);
const showDeleteResponsibilityDialog = ref(false);
const responsibilityActivationEmployeeCount = ref(0);

// Detach confirmation state — gates the tree-view detach entry point so the
// destructive detach call only fires after the user confirms.
const detachConfirm = ref({
    show: false,
    pendingDetachData: null,
    pendingDirectRow: null,
});

// Attach responsibility state
const showAttachResponsibilityDialog = ref(false);
const attachableResponsibilities = ref([]);
const hasMoreResponsibilitiesData = ref(false);
const isLoadingMoreResponsibilities = ref(false);
const isAssigningResponsibility = ref(false);
const selectedResponsibilityId = ref(null);
const currentRoleId = ref(null);
const responsibilitiesPage = ref(1);
const responsibilitiesSearchTerm = ref('');
const openDefaultAttachResponsibilityDialog = ref(false);

const showAttachResponsibilityToPositionDialog = ref(false);
const attachableResponsibilitiesForPosition = ref([]);
const hasMoreResponsibilitiesForPositionData = ref(false);
const isLoadingMoreResponsibilitiesForPosition = ref(false);
const isAssigningResponsibilityToPosition = ref(false);
const selectedPositionResponsibilityId = ref(null);
const responsibilitiesForPositionPage = ref(1);
const responsibilitiesForPositionSearchTerm = ref("");
const openDefaultAttachResponsibilityToPositionDialog = ref(false);

const showAttachRoleToPositionsBulkDialog = ref(false);
const bulkAttachPositions = ref([]);
const isBulkAttachPositionsLoading = ref(false);
const bulkAttachTotalPositions = ref(0);
const bulkAttachQuery = ref({
    page: 1,
    perPage: 10,
    search: "",
    statuses: [],
});
const bulkAttachedPositionIds = ref(new Set());

// Create position data object for the table component (reactive)
const positionDataObject = computed(() => ({
    id: props.positionId,
    name: props.positionName,
}));

// Position context for AddAndAttachRoleDialog ({ id, code, name }) — ER-12093
const addAndAttachPosition = computed(() => {
    const code = String(props.positionCode ?? '')
        .trim()
        .replace(/^#/, '')
        .split(' ')[0];
    if (!props.positionId || !props.positionName || !code) return null;
    return { id: props.positionId, code, name: props.positionName };
});

const detachContextObject = computed(() => ({
    id: props.positionId,
    type: "position",
    code: false,
    codeValue: "",
    name: props.positionName,
}));

// Copy for the detach confirmation dialog, matching the existing DetachRoleDialog /
// DetachResponsibilityDialog tone. Roles and responsibilities get slightly different
// wording; both reuse the shared destructive-dialog primitive.
const detachConfirmCopy = computed(() => {
    const { pendingDetachData, pendingDirectRow } = detachConfirm.value;
    if (pendingDetachData) {
        const detachObj = pendingDetachData.detachObj || {};
        const target = pendingDetachData.target || {};
        const detachType = (detachObj.type || detachObj.objectType || "").toLowerCase();
        if (detachType === "responsibility") {
            // Nested responsibility: `detachObj` is the responsibility being detached,
            // `target` is its parent role.
            const responsibilityName = detachObj?.name || detachObj?.objectName || "";
            const roleName = target?.name || target?.objectName || "";
            return {
                headline: "Detach Responsibility",
                icon: "org_icon_responsibility",
                info: `You are about to detach responsibility ${responsibilityName} from ${roleName}.\n\nThis action will irreversibly detach all assignments to this responsibility.`,
            };
        }
        const roleName = target?.name || target?.objectName || "";
        const parentName = detachObj?.name || detachObj?.objectName || "";
        return {
            headline: "Detach Role",
            icon: "org_icon_role",
            info: `You are about to detach Role ${roleName} from ${parentName}.\n\nThis action will irreversibly detach all assignments to this role as well as all assignments to responsibilities attached to this role.`,
        };
    }
    if (pendingDirectRow) {
        return {
            headline: "Detach Responsibility",
            icon: "org_icon_responsibility",
            info: `You are about to detach responsibility ${pendingDirectRow.name || ""} from ${props.positionName || ""}.\n\nThis action will irreversibly detach all assignments to this responsibility.`,
        };
    }
    return {
        headline: "Detach",
        icon: "org_icon_role",
        info: "",
    };
});

// Create a computed for pagination to ensure reactivity
const paginationComputed = computed(() => ({
    page: paginationModel.value.page,
    total: paginationModel.value.total,
    perPage: paginationModel.value.perPage,
}));

const attachedRoleNames = computed(() => rolesData.value.map(item => item.role_name));
const existingNames = computed(() => rolesData.value.map(role => role.role_name));
const renameRoleModel = computed(() => {
    const role = currentRole.value || {};
    return {
        id: role.id || role.data?.id || null,
        name: role.name || role.role_name || role.data?.name || "",
        context: role.context || role.data?.context || "",
        status: role.status || role.data?.status || "",
        archived: role.archived ?? role.data?.archived ?? false,
    };
});
const currentResponsibilityName = computed(() => currentResponsibility.value?.data?.name || "");
const archiveTarget = computed(() =>
    archiveObjectType.value === "responsibility" ? currentResponsibility.value : currentRole.value
);
const draftResponsibilityCount = computed(() => currentRole.value?.info?.draft_responsibilities_count ?? 0);
const showAttachResponsibilitiesPicker = computed(() => {
    return instance?.proxy?.$store?.getters?.["featureFlags/ideaMDP389"];
});

function parseFilters(filters) {
    const parsedFilters = convertRoleFiltersToApiFormat(filters || []);
    // Attached-roles views always exclude archived roles unless "show archived" is active.
    if (parsedFilters.archived === undefined) {
        parsedFilters.archived = false;
    }
    return parsedFilters;
}

function formatTreeCode(code) {
    const raw = String(code || "").trim();
    if (!raw) return "";
    const digits = raw.replace(/^#+/, "").split(" ")[0];
    return digits ? `#${digits}` : "";
}

function mapResponsibilityToTreeRow(resp) {
    return {
        id: resp.id,
        code: formatTreeCode(resp.code || resp.code_str),
        name: resp.name || "",
        type: "responsibility",
        status: resp.status || "active",
        archived: !!resp.archived,
        attachmentId: resp.responsibility_to_position_id || resp.attachment_id || resp.id,
        also_covered_by_roles: Array.isArray(resp.also_covered_by_roles)
            ? resp.also_covered_by_roles
            : [],
        children: [],
    };
}

function normalizeDirectAttachmentResults(response) {
    const data = response?.data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    if (data && typeof data === "object" && Array.isArray(data.data)) return data.data;
    return [];
}

function mapRoleToTreeRow(role, children = []) {
    const id = role.id ?? role.role_id ?? null;
    return {
        id,
        roleId: id,
        code: formatTreeCode(role.code || role.role_code),
        name: role.name || role.role_name || "",
        type: "role",
        status: role.status || "active",
        archived: !!role.archived,
        ...pickValidityFields(role),
        children,
    };
}

function findTreeRowById(rows, id, type) {
    for (const row of rows || []) {
        const idMatch = row.id === id || String(row.id) === String(id);
        const typeMatch = type == null || row.type === type;
        if (idMatch && typeMatch) return row;
        const found = findTreeRowById(row.children, id, type);
        if (found) return found;
    }
    return null;
}

async function fetchDirectResponsibilityRows() {
    if (!directResponsibilityAttachEnabled.value) return [];
    try {
        const response = await getDirectResponsibilitiesAttachedToPosition(
            accountId.value,
            props.positionId
        );
        return normalizeDirectAttachmentResults(response).map((resp) =>
            mapResponsibilityToTreeRow({
                id: resp.id,
                name: resp.name,
                code: resp.code,
                status: resp.status,
                archived: resp.archived,
                attachment_id: resp.attachment_id,
                responsibility_to_position_id: resp.attachment_id,
                also_covered_by_roles: resp.also_covered_by_roles || [],
            })
        );
    } catch (error) {
        console.error("Error fetching direct responsibilities for position:", error);
        return [];
    }
}

async function fetchResponsibilityChildrenForRole(roleId) {
    const response = await getListOfResponsibilitiesAttachedToRole(
        accountId.value,
        1000,
        0,
        "",
        roleId,
        { archived: treeFilters.value.archived === "Show" ? undefined : false }
    );
    return (response?.data?.results || []).map((resp) => ({
        ...mapResponsibilityToTreeRow(resp),
        parentId: roleId,
    }));
}

async function fetchRoleCatalogRowsForPosition(filters, search = "") {
    const response = await getListOfRolesAttachedToPosition(
        accountId.value,
        1000,
        0,
        search,
        props.positionId,
        filters
    );
    return (response?.data?.results || []).filter((role) => role.id != null);
}

async function fetchAttachedRoleRowsForReorder() {
    const response = await getRoleAttachmentsForPosition(
        accountId.value,
        props.positionId
    );
    const attachments = (response?.data?.results || response?.data || [])
        .filter((row) => row?.status === "attached")
        .sort(
            (a, b) =>
                (a.order ?? 0) - (b.order ?? 0) ||
                Number(a.id ?? 0) - Number(b.id ?? 0)
        );

    const [activeCatalogRows, archivedCatalogRows] = await Promise.all([
        fetchRoleCatalogRowsForPosition({
            status: ["draft", "active", "inactive", "retired"],
            archived: false,
        }),
        fetchRoleCatalogRowsForPosition({
            status: ["draft", "active", "inactive", "retired"],
            archived: true,
        }),
    ]);
    const catalogById = new Map(
        [...activeCatalogRows, ...archivedCatalogRows].map((role) => [
            String(role.id),
            role,
        ])
    );

    return attachments
        .map((attachment) => {
            const roleId = attachment.role ?? attachment.role_id;
            if (roleId == null) return null;
            const catalogRole = catalogById.get(String(roleId));
            const existing = findTreeRowById(treeRows.value, roleId);
            if (catalogRole) {
                return mapRoleToTreeRow(catalogRole, existing?.children || []);
            }
            if (existing?.type === "role") {
                return { ...existing, children: existing.children || [] };
            }
            return mapRoleToTreeRow(
                {
                    id: roleId,
                    name: existing?.name || "",
                    code: existing?.code || "",
                    status: existing?.status || "active",
                    archived: !!existing?.archived,
                },
                []
            );
        })
        .filter(Boolean);
}

async function fetchTopLevelAttachmentRows({ forReorder = false } = {}) {
    const directRows = await fetchDirectResponsibilityRows();
    if (forReorder) {
        const roleRows = await fetchAttachedRoleRowsForReorder();
        return [...directRows, ...roleRows];
    }

    const filters = parseFilters(appliedFilters.value);
    const search = useNewTree.value ? treeFilters.value.search : searchQuery.value;
    const roleRows = (await fetchRoleCatalogRowsForPosition(filters, search)).map((role) =>
        mapRoleToTreeRow(role, [])
    );
    return [...directRows, ...roleRows];
}

function mergeTopLevelRowsPreservingChildren(freshTopLevel, currentTree) {
    return freshTopLevel.map((freshRow) => {
        if (freshRow.type !== "role") return freshRow;
        const existing = (currentTree || []).find(
            (row) => row.type === "role" && String(row.id) === String(freshRow.id)
        );
        return existing ? { ...freshRow, children: existing.children || [] } : freshRow;
    });
}

// Reorder must send every attached role + direct responsibility. The live list
// fetch can lag behind or filter out rows the tree still shows, so keep the
// visible tree order and merge in any live rows the fetch added.
function buildTopLevelRowsForReorder(freshTopLevel, currentTree) {
    const freshById = new Map(
        (freshTopLevel || []).map((row) => [String(row.id), row])
    );
    const result = [];
    const seen = new Set();

    for (const row of currentTree || []) {
        const fresh = freshById.get(String(row.id));
        const entry = fresh
            ? fresh.type === "role"
                ? { ...fresh, children: row.children || fresh.children || [] }
                : fresh
            : row;
        result.push(entry);
        seen.add(String(entry.id));
    }

    for (const fresh of freshTopLevel || []) {
        if (seen.has(String(fresh.id))) continue;
        const existing = (currentTree || []).find(
            (row) => String(row.id) === String(fresh.id)
        );
        result.push(
            existing && fresh.type === "role"
                ? { ...fresh, children: existing.children || [] }
                : fresh
        );
    }

    return result;
}

async function buildTreeRows(roles) {
    const mapped = await Promise.all(
        (roles || []).map(async (role) => {
            const childCount =
                role.responsibility_attachment_count ??
                role.active_attached_responsibilities_count;
            if (!role.id) {
                return mapRoleToTreeRow(role, []);
            }
            if (childCount === 0) {
                return mapRoleToTreeRow(role, []);
            }
            try {
                const children = await fetchResponsibilityChildrenForRole(role.id);
                return mapRoleToTreeRow(role, children);
            } catch (error) {
                console.error("Error fetching responsibilities for role:", role.id, error);
                return mapRoleToTreeRow(role, []);
            }
        })
    );
    const roleRows = mapped.filter((row) => row.id != null);
    const directRows = await fetchDirectResponsibilityRows();
    return [...directRows, ...roleRows];
}

function mapRoleTableRow(el) {
    return {
        id: el.id ?? el.role_id ?? el.role?.id ?? null,
        role_code: el.code != null && el.code !== "" ? String(el.code).replace(/^#+/, "") : "",
        role_name: el.name || "",
        status: el.status || "inactive",
        attached_position_count: el.position_attachment_count || 0,
        attached_res_count: el.responsibility_attachment_count || 0,
        active_attached_position_count: el.active_attached_position_count || 0,
        inactive_attached_position_count: el.inactive_attached_position_count || 0,
        active_attached_responsibilities_count: el.active_attached_responsibilities_count || 0,
        inactive_attached_responsibilities_count: el.inactive_attached_responsibilities_count || 0,
        archived: el.archived || false,
        draft_responsibilities_count: el.draft_responsibilities_count || 0,
        ...pickValidityFields(el),
    };
}

async function fetchRoles() {
    if (!accountId.value || !props.positionId) {
        return;
    }
    
    try {
        isLoading.value = true;
        $loader?.show();
        
        const limit = useNewTree.value ? 1000 : paginationModel.value.perPage;
        const offset = useNewTree.value
            ? 0
            : (paginationModel.value.page - 1) * paginationModel.value.perPage;
        const filters = parseFilters(appliedFilters.value);
        const search = useNewTree.value ? "" : searchQuery.value;
        
        const response = await getListOfRolesAttachedToPosition(
            accountId.value,
            limit,
            offset,
            search,
            props.positionId,
            filters
        );
        
        if (response?.data) {
            const results = response.data.results || [];
            rolesData.value = results.map(mapRoleTableRow);
            paginationModel.value.total = response.data.count || 0;
            treeRows.value = useNewTree.value ? await buildTreeRows(results) : [];
            emit("position-attached-roles-sync", {
                positionId: props.positionId,
                roleIds: rolesData.value.map((r) => String(r.id)),
            });
        } else {
            rolesData.value = [];
            treeRows.value = [];
            paginationModel.value.total = 0;
            emit("position-attached-roles-sync", {
                positionId: props.positionId,
                roleIds: [],
            });
        }
    } catch (error) {
        console.error('Error fetching roles:', error);
        rolesData.value = [];
        treeRows.value = [];
        paginationModel.value.total = 0;
        emit("position-attached-roles-sync", {
            positionId: props.positionId,
            roleIds: [],
        });
    } finally {
        isLoading.value = false;
        $loader?.hide();
    }
}

async function handleReorderAttachedRoles({ data: reorderedPageRows }) {
    if (props.positionArchived) return;
    if (!accountId.value || !props.positionId || !reorderedPageRows?.length) {
        return;
    }
    try {
        $loader?.show();
        const total = paginationModel.value.total;
        if (!total) {
            return;
        }

        const filters = parseFilters(appliedFilters.value);
        const fullResp = await getListOfRolesAttachedToPosition(
            accountId.value,
            total,
            0,
            searchQuery.value,
            props.positionId,
            filters,
        );
        const fullResults = fullResp?.data?.results || [];
        const fullIds = fullResults.map((el) => el.id);

        const perPage = paginationModel.value.perPage;
        const offset = (paginationModel.value.page - 1) * perPage;
        const reorderedPageIds = reorderedPageRows.map((row) => row.id);

        const merged = [
            ...fullIds.slice(0, offset),
            ...reorderedPageIds,
            ...fullIds.slice(offset + reorderedPageIds.length),
        ];

        if (merged.length !== fullIds.length) {
            await fetchRoles();
            return;
        }

        await reorderRolesOnPosition(accountId.value, props.positionId, {
            role_order: merged,
        });

        $notifications?.success?.("Roles reordered", "", 3000);
        await fetchRoles();
        emit("refresh-position-data");
    } catch (error) {
        console.error("Error reordering attached roles:", error);
        $notifications?.error?.("Failed to reorder roles");
        await fetchRoles();
    } finally {
        $loader?.hide();
    }
}

function getAttachedRolesListSearch() {
    if (useNewTree.value) return treeFilters.value.search || "";
    const fromTable = organizationRolesTableRef.value?.searchQuery;
    if (typeof fromTable === "string") return fromTable;
    return searchQuery.value ?? "";
}

function handleTreeFiltersChange({ filters } = {}) {
    if (!filters) return;
    treeFilters.value = { ...treeFilters.value, ...filters };
    emit("attached-roles-search-sync", treeFilters.value.search ?? "");
}

function handleTreeApplyFilters({ filters } = {}) {
    treeFilters.value = {
        status: filters?.status || ["Active", "Draft"],
        archived: filters?.archived || "Do Not Show",
        types: filters?.types || [],
        search: treeFilters.value.search || "",
    };
}

function handleTreeResetFilters() {
    treeFilters.value = {
        status: ["Active", "Draft"],
        archived: "Do Not Show",
        types: treeFilters.value.types || [],
        search: treeFilters.value.search || "",
    };
}

function handleTreeClearFilters() {
    treeFilters.value = {
        status: ["Active", "Draft"],
        archived: "Do Not Show",
        types: [],
        search: "",
    };
    emit("attached-roles-search-sync", "");
}

function handleTreeViewChange({ view } = {}) {
    if (view === "list" || view === "tree") {
        treeView.value = view;
    }
}

function handleTreeItemSelected() {
    // Pane view renders RoleCard / ResponsibilityCard internally.
}

function handleTreeCardDrilldown(data) {
    emit("drilldown", data);
}

function moveSiblingRows(rows, fromId, toId, position = "before") {
    const from = rows.findIndex((row) => String(row.id) === String(fromId));
    const to = rows.findIndex((row) => String(row.id) === String(toId));
    if (from < 0 || to < 0) return null;
    if (position === "before" && from === to - 1) return null;
    if (position === "after" && from === to + 1) return null;
    if (from === to) return null;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    let insertAt = next.findIndex((row) => String(row.id) === String(toId));
    if (insertAt < 0) return null;
    if (position === "after") insertAt += 1;
    next.splice(insertAt, 0, moved);
    return next;
}

async function handleTreeReorder({ source, targetRow, position, kind } = {}) {
    if (props.positionArchived) return;

    const isNestedRespReorder =
        kind === "reorder-resp" &&
        (source?.parentType === "role" ||
            source?.parentId != null ||
            targetRow?.parentId != null);

    if (isNestedRespReorder) {
        await handleTreeReorderNestedResponsibilities({ source, targetRow, position });
        return;
    }

    if (kind === "reorder-role" || kind === "reorder-resp") {
        await handleTreeReorderTopLevel({ source, targetRow, position });
    }
}

async function handleTreeReorderNestedResponsibilities({ source, targetRow, position } = {}) {
    const roleId = source?.parentId ?? targetRow?.parentId;
    if (!roleId || !source?.rowId || !targetRow?.rowId) return;
    const roleRow = findTreeRowById(treeRows.value, roleId);
    if (!roleRow || !Array.isArray(roleRow.children)) return;

    // ER-11486: the in-memory tree can hold a responsibility that was detached
    // from this role elsewhere (another tab/session) after the tree was fetched.
    // Re-fetch the role's live attached responsibilities before building the
    // reorder payload so a stale/detached ID is never included in reordered_ids.
    let freshChildren;
    try {
        freshChildren = await fetchResponsibilityChildrenForRole(roleId);
    } catch (error) {
        console.error("Error re-fetching responsibilities for reorder:", error);
        $notifications?.error?.("Failed to reorder responsibilities");
        await reloadCurrentPage();
        return;
    }

    const nextChildren = moveSiblingRows(
        freshChildren,
        source.rowId,
        targetRow.rowId,
        position || "before"
    );

    // Source or target is no longer attached (or the move is a no-op): sync the
    // tree to the fresh list so the stale row disappears, without re-submitting.
    if (!nextChildren) {
        treeRows.value = treeRows.value.map((row) =>
            String(row.id) === String(roleId) ? { ...row, children: freshChildren } : row
        );
        return;
    }

    treeRows.value = treeRows.value.map((row) =>
        String(row.id) === String(roleId) ? { ...row, children: nextChildren } : row
    );

    const responsibilityOrder = nextChildren.map((row) => row.id);

    try {
        if (useNewTree.value) {
            await reorderRoleResponsibilities(accountId.value, roleId, responsibilityOrder);
        } else {
            await reOrderSequenceData(accountId.value, roleId, responsibilityOrder);
        }
        $notifications?.success?.("Responsibilities reordered", "", 3000);
    } catch (error) {
        console.error("Error reordering responsibilities:", error);
        $notifications?.error?.("Failed to reorder responsibilities");
        await reloadCurrentPage();
    }
}

async function handleTreeReorderTopLevel({ source, targetRow, position } = {}) {
    if (!source?.rowId || !targetRow?.rowId || !props.positionId) return;

    let freshRows;
    try {
        freshRows = await fetchTopLevelAttachmentRows({ forReorder: true });
    } catch (error) {
        console.error("Error re-fetching top-level attachments for reorder:", error);
        $notifications?.error?.("Failed to reorder attachments");
        await reloadCurrentPage();
        return;
    }

    const baseRows = buildTopLevelRowsForReorder(freshRows, treeRows.value);
    const baseIdSet = new Set(baseRows.map((row) => String(row.id)));
    if (
        !baseIdSet.has(String(source.rowId)) ||
        !baseIdSet.has(String(targetRow.rowId))
    ) {
        treeRows.value = baseRows;
        return;
    }
    const nextRows = moveSiblingRows(
        baseRows,
        source.rowId,
        targetRow.rowId,
        position || "before"
    );
    if (!nextRows) {
        treeRows.value = baseRows;
        return;
    }

    treeRows.value = nextRows;

    const attachments = nextRows.map((row) => ({
        type: row.type === "role" ? "role" : "responsibility",
        id: row.id,
    }));

    try {
        if (useNewTree.value) {
            await reorderPositionAttachments(accountId.value, props.positionId, attachments);
            $notifications?.success?.("Attachments reordered", "", 3000);
        } else {
            await reorderRolesOnPosition(accountId.value, props.positionId, {
                role_order: nextRows.filter((row) => row.type === "role").map((row) => row.id),
            });
            $notifications?.success?.("Roles reordered", "", 3000);
        }
    } catch (error) {
        console.error("Error reordering top-level attachments:", error);
        $notifications?.error?.(
            useNewTree.value ? "Failed to reorder attachments" : "Failed to reorder roles"
        );
        await reloadCurrentPage();
    }
}

function handleTreeDetach({ rowId, parentId, rowType } = {}) {
    if (props.positionArchived) return;
    const row = findTreeRowById(treeRows.value, rowId, rowType);
    if (!row) return;

    if (row.type === "role") {
        detachConfirm.value = {
            show: true,
            pendingDetachData: {
                target: {
                    objectId: rowId,
                    id: rowId,
                    objectType: "role",
                    type: "role",
                    name: row.name || "",
                },
                detachObj: {
                    id: props.positionId,
                    type: "position",
                    code: false,
                    codeValue: "",
                    name: props.positionName || "",
                },
            },
            pendingDirectRow: null,
        };
        return;
    }

    if (row.type === "responsibility" && parentId) {
        const parentRole = findTreeRowById(treeRows.value, parentId, "role");
        detachConfirm.value = {
            show: true,
            pendingDetachData: {
                target: {
                    objectId: parentId,
                    id: parentId,
                    objectType: "role",
                    type: "role",
                    name: parentRole?.name || "",
                },
                detachObj: {
                    id: rowId,
                    type: "responsibility",
                    name: row.name || "",
                },
            },
            pendingDirectRow: null,
        };
        return;
    }

    if (row.type === "responsibility") {
        detachConfirm.value = {
            show: true,
            pendingDetachData: null,
            pendingDirectRow: row,
        };
    }
}

/** Runs the detach the user confirmed, reusing the existing detach handlers unchanged. */
async function confirmDetach() {
    const { pendingDetachData, pendingDirectRow } = detachConfirm.value;
    detachConfirm.value.show = false;
    $loader?.setAppLoading(true);
    if (pendingDetachData) {
        await handleDetach(pendingDetachData);
    } else if (pendingDirectRow) {
        await handleDetachDirectResponsibility(pendingDirectRow);
    }
    detachConfirm.value.pendingDetachData = null;
    detachConfirm.value.pendingDirectRow = null;
}

/** Close/cancel the detach confirmation without making any API call. */
function closeDetachConfirm() {
    detachConfirm.value.show = false;
    detachConfirm.value.pendingDetachData = null;
    detachConfirm.value.pendingDirectRow = null;
}

async function handleDetachDirectResponsibility(row) {
    if (!directResponsibilityAttachEnabled.value) return;
    const attachmentId = row?.attachmentId;
    if (!attachmentId || !accountId.value) {
        $notifications?.error?.("Could not detach: missing attachment.");
        return;
    }

    try {
        $loader?.setAppLoading(true);
        await detachResponsibilityFromPosition(accountId.value, attachmentId);
        await reloadCurrentPage();
        emit("refresh-position-data");
        $notifications?.success?.("Responsibility successfully detached", "", 3000);
    } catch (error) {
        console.error("Error detaching responsibility from position:", error);
        const detail = error?.response?.data?.detail;
        $notifications?.error?.(
            typeof detail === "string" ? detail : "Failed to detach. Please try again."
        );
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function handleTreeOpenChatter({ rowId, rowType } = {}) {
    if (!rowId) return;
    const row = findTreeRowById(treeRows.value, rowId, rowType);
    const nextType = row?.type === "responsibility" ? "responsibility" : "role";
    if (currentChatterRoleId.value !== rowId || currentChatterType.value !== nextType) {
        chatterData.value = [];
        totalChatterData.value = 0;
        loadedChatterPages.value = {};
        currentChatterRoleId.value = rowId;
    }
    currentChatterType.value = nextType;
    treeChatterTypeValue.value = row?.name || "";
    treeChatterListData.value = { id: rowId, role_name: row?.name || "", status: row?.status };
    treeChatterOpen.value = true;
    treeChatterLoading.value = true;
    try {
        await fetchChatterData(rowId, nextType);
    } finally {
        treeChatterLoading.value = false;
    }
}

function handleTreeCloseChatter() {
    treeChatterOpen.value = false;
}

function handleSearchChange(searchText) {
    searchQuery.value = searchText;
    emit('attached-roles-search-sync', searchText ?? '');
    paginationModel.value.page = 1;
    fetchRoles();
}

function handleFilterChange(filters) {
    appliedFilters.value = filters;
    paginationModel.value.page = 1;
    fetchRoles();
}

function handlePageChange(pageData) {
    paginationModel.value.page = pageData.page;
    // perPage is maintained in paginationModel, only update if provided
    if (pageData.perPage) {
        paginationModel.value.perPage = pageData.perPage;
    }
    fetchRoles();
}

async function performRoleOperation(action) {
    switch (action) {
        case 'activate_role':
            await openActivateRoleConfirmation();
            break;
        case 'retired_role':
            showRetireRoleDialog.value = true;
            break;
        case 're_activate_role':
            await openReactivateRoleConfirmation();
            break;
        case 'attach_responsibility_role':
            currentRoleId.value = currentRole.value?.id || currentRole.value?.data?.id;
            openDefaultAttachResponsibilityDialog.value = true;
            showAttachResponsibilityDialog.value = true;
            responsibilitiesPage.value = 1;
            responsibilitiesSearchTerm.value = '';
            await fetchAttachableResponsibilitiesList();
            break;
        case 'rename_role':
            showRenameRoleDialog.value = true;
            break;
        case 'delete_role':
            showDeleteRoleDialog.value = true;
            break;
        case 'archive_action':
            archiveObjectType.value = 'role';
            archiveAction.value = 'archive';
            showArchiveDialog.value = true;
            break;
        case 'unarchive_action':
            archiveObjectType.value = 'role';
            archiveAction.value = 'unarchive';
            showArchiveDialog.value = true;
            break;
        case 'attach_role_positions_bulk':
            await openAttachRoleToPositionsBulkDialog();
            break;
        default:
            await reloadCurrentPage();
            emit('refresh-position-data');
    }
}

async function handleRowOperation(operation) {
    if (props.positionArchived) return;
    currentRole.value = operation.data;
    await performRoleOperation(operation.item?.value);
}

// The new-tree rows carry a flat shape (mapRoleToTreeRow), while the existing role dialogs
// read the legacy currentRole shape (id / data.id / data.name / info.draft_responsibilities_count).
// Build that shape on demand so the tree's role "⋯" menu reuses the exact same dialogs/handlers
// as OrganizationRolesTable instead of forking a second action path.
function buildCurrentRoleFromTreeRow(row) {
    const roleData = (rolesData.value || []).find(
        (r) => String(r.id) === String(row?.id ?? row?.roleId)
    );
    const id = row?.id ?? row?.roleId ?? null;
    return {
        id,
        role_name: row?.name || "",
        data: { id, name: row?.name || "" },
        info: { draft_responsibilities_count: roleData?.draft_responsibilities_count ?? 0 },
        status: row?.status,
        archived: row?.archived,
    };
}

// Same legacy shape the shared responsibility dialogs/handlers expect (id / realId / data.name).
function buildCurrentResponsibilityFromTreeRow(row) {
    const id = row?.id ?? null;
    return {
        id,
        realId: id,
        type: "responsibility",
        name: row?.name || "",
        data: { id, name: row?.name || "" },
        status: row?.status,
        archived: row?.archived,
    };
}

async function performResponsibilityOperation(action) {
    switch (action) {
        case 'activate_responsibility':
            await openResponsibilityActivationConfirmation('activate');
            break;
        case 'retire_responsibility':
            showRetireResponsibilityDialog.value = true;
            break;
        case 're_activate_responsibility':
            await openResponsibilityActivationConfirmation('reactivate');
            break;
        case 'rename_responsibility':
            showRenameResponsibilityDialog.value = true;
            break;
        case 'delete_responsibility':
            showDeleteResponsibilityDialog.value = true;
            break;
        case 'archive_action':
            archiveObjectType.value = 'responsibility';
            archiveAction.value = 'archive';
            showArchiveDialog.value = true;
            break;
        case 'unarchive_action':
            archiveObjectType.value = 'responsibility';
            archiveAction.value = 'unarchive';
            showArchiveDialog.value = true;
            break;
        default:
            break;
    }
}

async function handleTreeRowMenuAction({ row, value } = {}) {
    if (props.positionArchived) return;
    if (!row || !value) return;
    if (row.type === "role") {
        currentRole.value = buildCurrentRoleFromTreeRow(row);
        await performRoleOperation(value);
    } else if (row.type === "responsibility") {
        currentResponsibility.value = buildCurrentResponsibilityFromTreeRow(row);
        await performResponsibilityOperation(value);
    }
}

function handleRoleDrillDown(roleData) {
    if (!roleData?.id) return;

    if (!props.drilldownInPanel && instance?.proxy?.$router) {
        instance.proxy.$router.push({
            name: 'RoleDetailFromPosition',
            params: {
                positionId: props.positionId,
                roleId: roleData.id,
            },
            query: {
                positionName: props.positionName,
                roleTab: 'attached_responsibilities',
            },
        }).catch(() => {});
        return;
    }

    const drilldownPayload = {
        roleId: roleData.id,
        tab: 'attached_responsibilities',
        breadcrumbs: roleData.breadcrumbs,
        roleName: roleData.role_name ?? roleData.name,
        positionId: props.positionId,
    };
    emit('drilldown', drilldownPayload);
}

async function handleOpenChatterDrawer(data) {
    if (!data || !data.id) return;
    
    const roleId = data.id;
    
    // If switching to a different role, reset the chatter data
    currentChatterType.value = "role";
    if (currentChatterRoleId.value !== roleId) {
        chatterData.value = [];
        totalChatterData.value = 0;
        loadedChatterPages.value = {};
        currentChatterRoleId.value = roleId;
    }
    
    await fetchChatterData(roleId);
}

async function fetchChatterData(objectId, objectType = currentChatterType.value || "role") {
    if (!objectId || !accountId.value) return;
    
    const currentLength = chatterData.value.length;
    const page = Math.floor(currentLength / 10) + 1;
    
    // Initialize page tracking for this role if needed
    if (!loadedChatterPages.value[objectId]) {
        loadedChatterPages.value[objectId] = new Set();
    }
    
    // Don't reload already loaded pages
    if (loadedChatterPages.value[objectId].has(page)) {
        return;
    }
    
    try {
        const { data } = await getChatterList(accountId.value, objectId, objectType, page);
        loadedChatterPages.value[objectId].add(page);
        
        if (data && data.count) {
            totalChatterData.value = data.count;
        }
        
        if (data && data.results) {
            const chatterItems = data.results.map((el) => {
                const date = new Date(el.recorded_on);
                const milliseconds = date.getTime();
                const millisecondsString = milliseconds.toString();
                const nameParts = el.recorded_by_name.split(" ");
                
                const newEl = {
                    id: el.id,
                    IsExIQtiveBot: false,
                    message: el.note,
                    created_on: millisecondsString,
                    IsHistory: el.is_history,
                };
                
                if (nameParts.length === 2) {
                    newEl.avatar = {
                        picture: "",
                        firstName: nameParts[0],
                        lastName: nameParts[1],
                    };
                } else {
                    newEl.avatar = {
                        picture: "",
                        firstName: el.recorded_by_name,
                        lastName: "",
                    };
                }
                
                return newEl;
            });
            chatterData.value.push(...chatterItems);
        }
    } catch (error) {
        console.error('Error fetching chatter data:', error);
    }
}

async function reloadCurrentPage() {
    await fetchRoles();
}

function handleOpenAttachRoleDialog() {
    if (props.positionArchived) return;
    openDefaultAttachDialog.value = true;
    showAttachRoleDialog.value = true;
    rolesPage.value = 1;
    rolesSearchTerm.value = '';
    fetchAttachableRolesList();
}

function handleImportJobDescription() {
    if (!directResponsibilityAttachEnabled.value || props.positionArchived) return;
    iqOverlayStore.openJobDescriptionUpload({
        positionId: props.positionId,
        positionName: props.positionName,
    });
}

watch(
    () => iqOverlayStore.isOpen,
    async (open, wasOpen) => {
        if (wasOpen && !open) {
            await fetchRoles();
        }
    }
);

function collectTreeResponsibilityIds(rows, ids = new Set()) {
    (rows || []).forEach((row) => {
        if (row?.type === "responsibility" && row.id != null) {
            ids.add(String(row.id));
        }
        if (row?.children?.length) {
            collectTreeResponsibilityIds(row.children, ids);
        }
    });
    return ids;
}

function handleOpenAttachResponsibilityToPositionDialog() {
    if (!directResponsibilityAttachEnabled.value || props.positionArchived) return;
    openDefaultAttachResponsibilityToPositionDialog.value = true;
    showAttachResponsibilityToPositionDialog.value = true;
    responsibilitiesForPositionPage.value = 1;
    responsibilitiesForPositionSearchTerm.value = "";
    fetchAttachableResponsibilitiesForPositionList();
}

async function fetchAttachableRolesList(isLoadMore = false) {
    if (!props.positionId || !accountId.value) return;

    const limit = 10;
    const offset = (rolesPage.value - 1) * limit;
    const name = rolesSearchTerm.value || '';

    try {
        if (!isLoadMore) {
            attachableRoles.value = [];
        }
        isLoadingMoreRoles.value = true;

        const response = await fetchAttachableRolesForPosition(
            accountId.value,
            props.positionId,
            limit,
            offset,
            name
        );

        const newItems = (response?.data?.results || []).map((item) => {
            const code = item.code || item.role_code || item.code_str?.match(/\d+/)?.[0] || '';
            return {
                ...item,
                id: item.id,
                role_code: code,
                code: code,
                name: item.name,
                status: item.status,
                attached: item?.attached_to_position || false,
                disabled: item?.attached_to_position || false,
            };
        });

        attachableRoles.value = isLoadMore
            ? [...attachableRoles.value, ...newItems]
            : newItems;

        const totalCount = response?.data?.count || 0;
        hasMoreRolesData.value = (rolesPage.value * limit) < totalCount;
    } catch (error) {
        console.error("Error fetching attachable roles:", error);
    } finally {
        isLoadingMoreRoles.value = false;
    }
}

function handleRoleSelected(role) {
    if (role && Object.prototype.hasOwnProperty.call(role, "id")) {
        selectedRoleId.value = role.id;
    }
}

async function handleAddNewRole(item) {
    try {
        $loader?.show();
        const payload = {
            name: item.name,
            account: accountId.value,
        };

        const { data } = await createRole(accountId.value, payload);
        selectedRoleId.value = data.id;
        $loader?.hide();

        await attachSelectedRoleToPosition();
    } catch (error) {
        console.error("Error creating role:", error);
        $loader?.hide();
    }
}

async function attachSelectedRoleToPosition() {
    if (!selectedRoleId.value || !props.positionId) return;

    try {
        isAssigningRole.value = true;
        $loader?.setAppLoading(true);

        await attachRoleToPosition(accountId.value, props.positionId, {
            attachment_id: selectedRoleId.value,
        });

        showAttachRoleDialog.value = false;
        selectedRoleId.value = null;
        rolesPage.value = 1;
        rolesSearchTerm.value = '';
        attachRoleError.value = "";

        await reloadCurrentPage();
        emit('refresh-position-data');
    } catch (error) {
        console.error("Error attaching role:", error);
        attachRoleError.value = error.response?.data?.[0] || "An error occurred while attaching the role";
    } finally {
        isAssigningRole.value = false;
        $loader?.setAppLoading(false);
    }
}

/**
 * AddAndAttachRoleDialog already created the Draft Role and attached it to the
 * Position in one atomic call (ER-12095) — just refresh the list.
 */
async function handleRoleCreatedAndAttached() {
    showAttachRoleDialog.value = false;
    selectedRoleId.value = null;
    rolesPage.value = 1;
    rolesSearchTerm.value = '';
    attachRoleError.value = "";

    await reloadCurrentPage();
    emit('refresh-position-data');
}

function loadMoreRoles() {
    if (isLoadingMoreRoles.value || !hasMoreRolesData.value) {
        return;
    }
    rolesPage.value += 1;
    fetchAttachableRolesList(true);
}

function searchRoles(searchTerm) {
    rolesSearchTerm.value = searchTerm;
    rolesPage.value = 1;
    fetchAttachableRolesList(false);
}

function closeAttachRoleDialog() {
    showAttachRoleDialog.value = false;
    openDefaultAttachDialog.value = false;
    selectedRoleId.value = null;
    rolesPage.value = 1;
    rolesSearchTerm.value = '';
    attachRoleError.value = "";
}

const onActivateRoleConfirm = () => {
    const draftCount = draftResponsibilityCount.value;
    showActivateRoleDialog.value = false;
    if (draftCount > 0) {
        showActivateResponsibility.value = true;
        return;
    }
    processRoleActivation(false);
};

async function processRoleActivation(activateResponsibilities = false) {
    try {
        $loader?.setAppLoading(true);
        if (activateResponsibilities) {
            activateAttachedButtonLoader.value = true;
        } else {
            skipActivateButtonLoader.value = true;
        }

        const idRole = currentRole.value?.id || currentRole.value?.data?.id;
        const payload = {
            account_id: accountId.value,
            role_id: idRole,
            ...(activateResponsibilities && { activate_responsibilities: true }),
        };
        await updateActiveRole(accountId.value, idRole, payload);
        showActivateResponsibility.value = false;
        currentRole.value = null;
        await reloadCurrentPage();
        emit('refresh-position-data');
    } catch (error) {
        console.error('Error activating role:', error);
        showActivateResponsibility.value = false;
    } finally {
        $loader?.setAppLoading(false);
        skipActivateButtonLoader.value = false;
        activateAttachedButtonLoader.value = false;
    }
}

function skipActivateResponsibility() {
    processRoleActivation(false);
}

function activateAttachedResponsibilities() {
    processRoleActivation(true);
}

async function retireRole() {
    try {
        $loader?.setAppLoading(true);
        const idRole = currentRole.value?.id || currentRole.value?.data?.id;
        const nameRole = currentRole.value?.data?.name;
        await updateRetiredRole(accountId.value, idRole, {
            name: nameRole,
            status: "retired",
            account: accountId.value,
        });
        showRetireRoleDialog.value = false;
        currentRole.value = null;
        await reloadCurrentPage();
        emit('refresh-position-data');
    } catch (error) {
        console.error('Error retiring role:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function reActivateRole() {
    try {
        $loader?.setAppLoading(true);
        const idRole = currentRole.value?.id || currentRole.value?.data?.id;
        const nameRole = currentRole.value?.data?.name;
        await updateReActiveRole(accountId.value, idRole, {
            name: nameRole,
            status: "inactive",
            account: accountId.value,
        });
        showReActivateRoleDialog.value = false;
        currentRole.value = null;
        await reloadCurrentPage();
        emit('refresh-position-data');
    } catch (error) {
        console.error('Error re-activating role:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function onRoleRenamed() {
    try {
        $loader?.setAppLoading(true);
        showRenameRoleDialog.value = false;
        currentRole.value = null;
        await reloadCurrentPage();
        emit('refresh-position-data');
    } catch (error) {
        console.error('Error refreshing roles after rename:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

// ER-11818 / Roles Rules 3.4 & 3.6 — before activating/reactivating a Role, ask the
// backend how many employees will be assigned (N). N > 0 opens the existing confirmation
// dialog (now with the count line); N = 0 skips it and proceeds directly. Preview failure
// degrades to the pre-existing dialog flow rather than blocking activation while the
// backend endpoint (ER-11822) is still behind its own ticket.
async function fetchRoleActivationEmployeeCount(kind) {
    roleActivationEmployeeCount.value = 0;
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    // null = "unknown": the preview could not be determined, so the caller falls back to
    // the pre-existing confirmation dialog. Only a successful preview with N = 0 skips it.
    return fetchRoleActivationCount(kind, accountId.value, idRole);
}

async function openActivateRoleConfirmation() {
    const count = await fetchRoleActivationEmployeeCount('activate');
    roleActivationEmployeeCount.value = count ?? 0;
    if (count === 0) {
        await onActivateRoleConfirm();
        return;
    }
    showActivateRoleDialog.value = true;
}

async function openReactivateRoleConfirmation() {
    const count = await fetchRoleActivationEmployeeCount('reactivate');
    roleActivationEmployeeCount.value = count ?? 0;
    if (count === 0) {
        await reActivateRole();
        return;
    }
    showReActivateRoleDialog.value = true;
}

async function deleteRole(deleteDraftResponsibilities = false) {
    try {
        $loader?.setAppLoading(true);
        const idRole = currentRole.value?.id || currentRole.value?.data?.id;
        const payload = {
            delete_draft_responsibilities: deleteDraftResponsibilities,
        };

        if (deleteDraftResponsibilities) {
            await deleteReadinessRoleWithResponsibilities(accountId.value, idRole, payload);
        } else {
            await deleteReadinessRole(accountId.value, idRole);
        }

        showDeleteRoleDialog.value = false;
        currentRole.value = null;
        await reloadCurrentPage();
        emit('refresh-position-data');
    } catch (error) {
        console.error('Error deleting role:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function refreshAfterResponsibilityAction() {
    currentResponsibility.value = null;
    await reloadCurrentPage();
    emit('refresh-position-data');
}

// ER-11818 — N > 0 opens the confirmation (with the count); N = 0 proceeds directly; an
// undeterminable preview (null) falls back to the confirmation dialog.
async function openResponsibilityActivationConfirmation(kind) {
    responsibilityActivationEmployeeCount.value = 0;
    const count = await fetchResponsibilityActivationCount(
        kind,
        accountId.value,
        currentResponsibility.value?.realId
    );
    responsibilityActivationEmployeeCount.value = count ?? 0;
    if (count === 0) {
        await (kind === 'activate' ? onActivateResponsibility() : onReActivateResponsibility());
        return;
    }
    if (kind === 'activate') showActivateResponsibilityDialog.value = true;
    else showReActivateResponsibilityDialog.value = true;
}

async function onActivateResponsibility() {
    try {
        $loader?.setAppLoading(true);
        const id = currentResponsibility.value?.realId;
        await updateActivateResponsibility(accountId.value, id, {
            account_id: accountId.value,
            type: "responsibility",
            obj_id: id,
        });
        showActivateResponsibilityDialog.value = false;
        await refreshAfterResponsibilityAction();
    } catch (error) {
        console.error('Error activating responsibility:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function onRetireResponsibility() {
    try {
        $loader?.setAppLoading(true);
        await updateRetireResponsibility(accountId.value, currentResponsibility.value?.realId, {
            status: "retired",
            name: currentResponsibility.value?.data?.name,
            account: accountId.value,
        });
        showRetireResponsibilityDialog.value = false;
        await refreshAfterResponsibilityAction();
    } catch (error) {
        console.error('Error retiring responsibility:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function onReActivateResponsibility() {
    try {
        $loader?.setAppLoading(true);
        await updateReActivateResponsibility(accountId.value, currentResponsibility.value?.realId, {
            status: "active",
            name: currentResponsibility.value?.data?.name,
            account: accountId.value,
        });
        showReActivateResponsibilityDialog.value = false;
        await refreshAfterResponsibilityAction();
    } catch (error) {
        console.error('Error re-activating responsibility:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function onResponsibilityRenamed() {
    try {
        $loader?.setAppLoading(true);
        await refreshAfterResponsibilityAction();
    } catch (error) {
        console.error('Error refreshing after responsibility rename:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function onDeleteResponsibility() {
    try {
        $loader?.setAppLoading(true);
        await deleteResponsibility(accountId.value, currentResponsibility.value?.realId);
        showDeleteResponsibilityDialog.value = false;
        await refreshAfterResponsibilityAction();
    } catch (error) {
        console.error('Error deleting responsibility:', error);
        $notifications?.error?.(getDeleteErrorMessage(error, { entity: "responsibility" }));
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function onArchiveResponsibility() {
    try {
        $loader?.setAppLoading(true);
        const id = currentResponsibility.value?.realId;
        if (archiveAction.value === 'archive') {
            await updateArchiveResponsibility(accountId.value, 'responsibility', id);
        } else {
            await updateUnArchiveResponsibility(accountId.value, 'responsibility', id);
        }
        showArchiveDialog.value = false;
        await refreshAfterResponsibilityAction();
    } catch (error) {
        console.error('Error archiving/unarchiving responsibility:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function onArchiveConfirm() {
    if (archiveObjectType.value === 'responsibility') {
        await onArchiveResponsibility();
        return;
    }
    await onArchiveRole();
}

async function onArchiveRole() {
    try {
        $loader?.setAppLoading(true);
        const idRole = currentRole.value?.id || currentRole.value?.data?.id;

        if (archiveAction.value === 'archive') {
            await updateArchiveRole(accountId.value, 'role', idRole);
        } else {
            await updateUnArchiveRole(accountId.value, 'role', idRole);
        }

        showArchiveDialog.value = false;
        currentRole.value = null;
        await reloadCurrentPage();
        emit('refresh-position-data');
    } catch (error) {
        console.error('Error archiving/unarchiving role:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function fetchAttachableResponsibilitiesList(isLoadMore = false) {
    if (!currentRoleId.value || !accountId.value) return;

    const limit = 10;
    const offset = (responsibilitiesPage.value - 1) * limit;
    const name = responsibilitiesSearchTerm.value || '';

    try {
        if (!isLoadMore) {
            attachableResponsibilities.value = [];
        }
        isLoadingMoreResponsibilities.value = true;

        const response = await fetchAttachableResponsibilities(
            accountId.value,
            currentRoleId.value,
            limit,
            offset,
            name
        );

        const newItems = (response?.data?.results || []).map((item) => ({
            id: item.id,
            code: item.code_str?.match(/\d+/)?.[0] || '',
            name: item.name,
            status: item.status,
            attached: item?.attached_to_role,
        }));

        attachableResponsibilities.value = isLoadMore
            ? [...attachableResponsibilities.value, ...newItems]
            : newItems;

        const totalCount = response?.data?.count || 0;
        hasMoreResponsibilitiesData.value = (responsibilitiesPage.value * limit) < totalCount;
    } catch (error) {
        console.error("Error fetching attachable responsibilities:", error);
    } finally {
        isLoadingMoreResponsibilities.value = false;
    }
}

function handleResponsibilitySelected(responsibility) {
    if (responsibility && Object.prototype.hasOwnProperty.call(responsibility, "id")) {
        selectedResponsibilityId.value = responsibility.id;
    }
}

async function handleAddNewResponsibility(item) {
    try {
        $loader?.show();
        const payload = {
            name: item.name,
            accountId: accountId.value,
        };
        const newResponsibility = await instance?.proxy?.$store?.dispatch(
            "readinessCatalogsStore/createResponsibility",
            payload
        );
        selectedResponsibilityId.value = newResponsibility.data.id;
        $loader?.hide();
        await handleAttachResponsibilityToRole();
    } catch (error) {
        console.error("Error creating responsibility:", error);
        $loader?.hide();
    }
}

async function handleAttachResponsibilityToRole() {
    if (!selectedResponsibilityId.value || !currentRoleId.value) return;

    try {
        isAssigningResponsibility.value = true;
        $loader?.setAppLoading(true);

        await attachResponsibilityToRole(accountId.value, currentRoleId.value, {
            attachment_id: selectedResponsibilityId.value,
        });

        showAttachResponsibilityDialog.value = false;
        selectedResponsibilityId.value = null;
        responsibilitiesPage.value = 1;
        responsibilitiesSearchTerm.value = '';
        await reloadCurrentPage();
        emit('refresh-position-data');
    } catch (error) {
        console.error("Error attaching responsibility:", error);
    } finally {
        isAssigningResponsibility.value = false;
        $loader?.setAppLoading(false);
    }
}

async function loadMoreResponsibilities() {
    if (isLoadingMoreResponsibilities.value || !hasMoreResponsibilitiesData.value) {
        return;
    }
    responsibilitiesPage.value += 1;
    await fetchAttachableResponsibilitiesList(true);
}

async function searchResponsibilities(searchTerm) {
    responsibilitiesSearchTerm.value = searchTerm;
    responsibilitiesPage.value = 1;
    await fetchAttachableResponsibilitiesList(false);
}

function closeAttachResponsibilityDialog() {
    showAttachResponsibilityDialog.value = false;
    openDefaultAttachResponsibilityDialog.value = false;
    selectedResponsibilityId.value = null;
    responsibilitiesPage.value = 1;
    responsibilitiesSearchTerm.value = '';
}

async function fetchAttachableResponsibilitiesForPositionList(isLoadMore = false) {
    if (!accountId.value) return;

    const limit = 10;
    const offset = (responsibilitiesForPositionPage.value - 1) * limit;
    const name = responsibilitiesForPositionSearchTerm.value || "";
    const alreadyAttached = collectTreeResponsibilityIds(treeRows.value);

    try {
        if (!isLoadMore) {
            attachableResponsibilitiesForPosition.value = [];
        }
        isLoadingMoreResponsibilitiesForPosition.value = true;

        const response = await fetchAttachableResponsibilitiesForPosition(
            accountId.value,
            limit,
            offset,
            name
        );
        const newItems = (response?.data?.results || []).map((item) => {
            const attached = alreadyAttached.has(String(item.id));
            return {
                ...item,
                id: item.id,
                attached,
                disabled: attached,
            };
        });
        attachableResponsibilitiesForPosition.value = isLoadMore
            ? [...attachableResponsibilitiesForPosition.value, ...newItems]
            : newItems;
        const totalCount = response?.data?.count || 0;
        hasMoreResponsibilitiesForPositionData.value =
            responsibilitiesForPositionPage.value * limit < totalCount;
    } catch (error) {
        console.error("Error fetching attachable responsibilities for position:", error);
    } finally {
        isLoadingMoreResponsibilitiesForPosition.value = false;
    }
}

function handlePositionResponsibilitySelected(responsibility) {
    if (responsibility && Object.prototype.hasOwnProperty.call(responsibility, "id")) {
        selectedPositionResponsibilityId.value = responsibility.id;
    }
}

async function handleAddNewResponsibilityToPosition(item) {
    try {
        $loader?.show();
        const payload = {
            name: item.name,
            accountId: accountId.value,
        };
        const newResponsibility = await instance?.proxy?.$store?.dispatch(
            "readinessCatalogsStore/createResponsibility",
            payload
        );
        selectedPositionResponsibilityId.value = newResponsibility.data.id;
        $loader?.hide();
        await handleAttachResponsibilityToPosition();
    } catch (error) {
        console.error("Error creating responsibility:", error);
        $loader?.hide();
    }
}

function attachResponsibilityToPositionErrorMessage(error) {
    const data = error?.response?.data || {};
    const code = typeof data.error === "string" ? data.error : data.error?.[0];
    const detail = typeof data.detail === "string" ? data.detail : data.detail?.[0];
    const roleName = data.details?.role_name;

    if (code === "RESPONSIBILITY_ALREADY_COVERED_BY_ROLE") {
        return roleName
            ? `This responsibility is already covered by the role ${roleName}`
            : "This responsibility is already covered by an attached role";
    }
    if (code === "RESPONSIBILITY_NOT_ATTACHABLE") {
        return detail || "This responsibility cannot be attached to the position";
    }
    if (code === "POSITION_NOT_ATTACHABLE") {
        return detail || "This position cannot receive responsibility attachments";
    }
    return detail || (typeof data.error === "string" ? data.error : "Failed to attach responsibility");
}

async function handleAttachResponsibilityToPosition() {
    if (!directResponsibilityAttachEnabled.value) return;
    if (!selectedPositionResponsibilityId.value || !props.positionId) return;

    try {
        isAssigningResponsibilityToPosition.value = true;
        $loader?.setAppLoading(true);

        await attachResponsibilityToPosition(
            accountId.value,
            props.positionId,
            selectedPositionResponsibilityId.value
        );

        showAttachResponsibilityToPositionDialog.value = false;
        selectedPositionResponsibilityId.value = null;
        responsibilitiesForPositionPage.value = 1;
        responsibilitiesForPositionSearchTerm.value = "";
        await reloadCurrentPage();
        emit("refresh-position-data");
        $notifications?.success?.("Responsibility successfully attached", "", 3000);
    } catch (error) {
        console.error("Error attaching responsibility to position:", error);
        $notifications?.error?.(attachResponsibilityToPositionErrorMessage(error));
    } finally {
        isAssigningResponsibilityToPosition.value = false;
        $loader?.setAppLoading(false);
    }
}

function loadMoreResponsibilitiesForPosition() {
    if (isLoadingMoreResponsibilitiesForPosition.value || !hasMoreResponsibilitiesForPositionData.value) {
        return;
    }
    responsibilitiesForPositionPage.value += 1;
    fetchAttachableResponsibilitiesForPositionList(true);
}

function searchResponsibilitiesForPosition(searchTerm) {
    responsibilitiesForPositionSearchTerm.value = searchTerm;
    responsibilitiesForPositionPage.value = 1;
    fetchAttachableResponsibilitiesForPositionList(false);
}

function closeAttachResponsibilityToPositionDialog() {
    showAttachResponsibilityToPositionDialog.value = false;
    openDefaultAttachResponsibilityToPositionDialog.value = false;
    selectedPositionResponsibilityId.value = null;
    responsibilitiesForPositionPage.value = 1;
    responsibilitiesForPositionSearchTerm.value = "";
}

async function handleDetach(detachData) {
    if (props.positionArchived) return;
    try {
        // Note: The modal already sets $loader.setAppLoading(true) when detach is clicked
        
        const targetObj = detachData.target;
        const detachObj = detachData.detachObj;
        
        if (!accountId.value) {
            accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
        }

        const roleId = targetObj?.objectId ?? targetObj?.id;
        const roleType = (targetObj?.objectType || targetObj?.type || "").toLowerCase();
        const detachType = (detachObj?.type || detachObj?.objectType || "").toLowerCase();

        if (!roleId || !detachObj?.id) {
            console.error("handleDetach: missing role or parent id", { targetObj, detachObj });
            $notifications?.error?.("Could not detach: missing role or position.");
            return;
        }
        
        if (roleType === "role") {
            if (detachType === "position") {
                await detachRoleAttachment(accountId.value, roleId, {
                    parent_id: detachObj.id,
                });
            } else if (detachType === "responsibility") {
                const payload = {
                    childType: "responsibility",
                    childId: detachObj.id,
                    parentData: {
                        parent_id: roleId,
                    },
                };
                await detachOrgObject(accountId.value, payload);
            } else {
                console.warn("handleDetach: unsupported detach type for role", detachType);
                $notifications?.error?.("Could not detach: unsupported attachment type.");
                return;
            }
        } else {
            console.warn("handleDetach: unsupported target type", roleType);
            $notifications?.error?.("Could not detach: unsupported target.");
            return;
        }
        
        // Reload the roles data to reflect the changes
        await fetchRoles();
        
        // Emit event to parent to refetch position data and update count
        emit('refresh-position-data');
        
        // Call the callback function to close the modal AFTER reloading data
        if (detachData.callBackFunc) {
            detachData.callBackFunc();
        }
    } catch (error) {
        console.error("Error detaching:", error);
        const detail = error?.response?.data?.detail;
        $notifications?.error?.(
            typeof detail === "string" ? detail : "Failed to detach. Please try again."
        );
    } finally {
        // Turn off the loader to re-enable buttons
        instance?.proxy?.$loader.setAppLoading(false);
    }
}

/** When detach context was missing on the table, still detach using this tab's position props. */
function handleDetachInline({ target }) {
    if (!target?.objectId && !target?.id) {
        return;
    }
    const normalizedTarget = {
        ...target,
        objectId: target.objectId ?? target.id,
        objectType: target.objectType || "Role",
    };
    detachConfirm.value = {
        show: true,
        pendingDetachData: {
            target: normalizedTarget,
            detachObj: {
                id: props.positionId,
                type: "position",
                code: false,
                codeValue: "",
                name: props.positionName || "",
            },
            callBackFunc: () => {
                organizationRolesTableRef.value?.handleInlineDetachComplete?.();
            },
        },
        pendingDirectRow: null,
    };
}

async function handleRequestMoreCheckpointsChatter(data) {
    if (currentChatterRoleId.value) {
        await fetchChatterData(currentChatterRoleId.value);
    }
}

async function loadAttachedPositionIdsForRole(roleId) {
    const ids = new Set();
    const limit = 100;
    let offset = 0;
    let count = 0;
    do {
        const response = await getListOfPositionsAttachedToRole(
            accountId.value,
            roleId,
            limit,
            offset,
            "",
            {}
        );
        const results = response?.data?.results || [];
        count = response?.data?.count || 0;
        results.forEach((item) => ids.add(item.id));
        offset += limit;
    } while (offset < count);
    return ids;
}

async function loadBulkAttachPositionsPage({
    page = bulkAttachQuery.value.page,
    perPage = bulkAttachQuery.value.perPage,
    search = bulkAttachQuery.value.search,
    statuses = bulkAttachQuery.value.statuses,
} = {}) {
    const offset = (page - 1) * perPage;
    const queryParamsObj = {
        limit: perPage,
        offset,
        archived: false,
        ordering: "name",
        name: search || undefined,
    };
    if (statuses && statuses.length > 0) {
        queryParamsObj.status = statuses;
    }
    const response = await getListOfPositionsV2({
        account_id: accountId.value,
        queryParamsObj,
    });
    const results = response?.data?.results || [];
    const totalCount = response?.data?.count || 0;
    bulkAttachTotalPositions.value = totalCount;
    bulkAttachPositions.value = results.map((position) => ({
        id: position.id,
        code:
            position.code ??
            String(position.code_str || "").replace("#", "").split(" ")[0] ??
            "",
        name: position.name,
        status: position.status || "draft",
        attached: bulkAttachedPositionIds.value.has(position.id),
        attachmentsCount:
            position.attachments_count?.role ??
            position.roles_count ??
            0,
        rolesCount:
            position.attachments_count?.role ??
            position.roles_count ??
            0,
        responsibilitiesCount:
            position.attachments_count?.responsibility ??
            position.responsibilities_count ??
            0,
    }));
}

async function handleBulkAttachPositionRequest(requestPayload = {}) {
    if (!showAttachRoleToPositionsBulkDialog.value) return;
    bulkAttachQuery.value = {
        ...bulkAttachQuery.value,
        ...requestPayload,
    };
    isBulkAttachPositionsLoading.value = true;
    try {
        await loadBulkAttachPositionsPage(bulkAttachQuery.value);
    } catch (error) {
        console.error("Error loading bulk attach positions page:", error);
    } finally {
        isBulkAttachPositionsLoading.value = false;
    }
}

async function openAttachRoleToPositionsBulkDialog() {
    const roleId = currentRole.value?.id || currentRole.value?.data?.id;
    if (!roleId || !accountId.value) return;
    showAttachRoleToPositionsBulkDialog.value = true;
    bulkAttachQuery.value = {
        page: 1,
        perPage: 10,
        search: "",
        statuses: [],
    };
    bulkAttachedPositionIds.value = new Set();
    bulkAttachTotalPositions.value = 0;
    bulkAttachPositions.value = [];
    isBulkAttachPositionsLoading.value = true;
    try {
        loadAttachedPositionIdsForRole(roleId)
            .then((ids) => {
                bulkAttachedPositionIds.value = ids;
                bulkAttachPositions.value = bulkAttachPositions.value.map((position) => ({
                    ...position,
                    attached: ids.has(position.id),
                }));
            })
            .catch((error) => {
                console.error("Error loading attached position IDs:", error);
            });
        await loadBulkAttachPositionsPage(bulkAttachQuery.value);
    } catch (error) {
        console.error("Error opening bulk attach dialog:", error);
    } finally {
        isBulkAttachPositionsLoading.value = false;
    }
}

async function handleBulkAttachRoleToPositions(payload) {
    const positionIds = payload?.positionIds || [];
    const roleId = currentRole.value?.id || currentRole.value?.data?.id;
    if (!roleId || positionIds.length === 0) return;
    instance?.proxy?.$loader?.setAppLoading(true);
    try {
        await Promise.all(
            positionIds.map((positionId) =>
                attachRoleToPosition(accountId.value, positionId, {
                    attachment_id: roleId,
                })
            )
        );
        $notifications?.success?.("Role attached to selected positions Successfully.");
        showAttachRoleToPositionsBulkDialog.value = false;
        await reloadCurrentPage();
        emit("refresh-position-data");
    } catch (error) {
        console.error("Error attaching role to positions in bulk:", error);
    } finally {
        instance?.proxy?.$loader?.setAppLoading(false);
    }
}

function closeAttachRoleToPositionsBulkDialog() {
    showAttachRoleToPositionsBulkDialog.value = false;
    isBulkAttachPositionsLoading.value = false;
    bulkAttachTotalPositions.value = 0;
    bulkAttachedPositionIds.value = new Set();
    bulkAttachPositions.value = [];
}

function onBulkAttachAddPosition() {
    closeAttachRoleToPositionsBulkDialog();
    instance?.proxy?.$router
        ?.push({ name: "NewOrganization", params: { tab: "Positions" } })
        .catch(() => {});
}


// Expose method to parent to refresh roles data
function refreshRoles() {
    return fetchRoles();
}

// Expose the refresh method to parent components
defineExpose({
    refreshRoles,
    getAttachedRolesListSearch,
});

onMounted(async () => {
    try {
        accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
        let treeKillSwitchOn = true;
        let ideaMdp585On = false;
        try {
            treeKillSwitchOn =
                (await instance?.proxy?.$getFlag?.(
                    LD_FLAG_POSITION_ROLES_RESPONSIBILITIES_TREE,
                    true
                )) !== false;
        } catch {
            treeKillSwitchOn = true;
        }
        try {
            ideaMdp585On =
                (await instance?.proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_585, false)) === true;
        } catch {
            ideaMdp585On = false;
        }
        // idea-MDP-585 is the rollout gate for the new tree (direct attach, drag/drop,
        // Import Job Description). Off → legacy OrganizationRolesTable.
        try {
            validityIndicatorsEnabled.value =
                (await instance?.proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_495, true)) !== false;
        } catch {
            validityIndicatorsEnabled.value = true;
        }
        useNewTree.value = treeKillSwitchOn && ideaMdp585On;
        directResponsibilityAttachEnabled.value = ideaMdp585On;
        try {
            skeletonLoadingEnabled.value =
                (await instance?.proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
        } catch {
            skeletonLoadingEnabled.value = false;
        }
        if (props.positionId && accountId.value) {
            await fetchRoles();
        }
    } catch (error) {
        console.error('Error initializing component:', error);
    }
});
</script>
